/**
 * Monthly rates, charged monthly (Ned, 2026-08-31).
 *
 * Every price in the product is quoted PER MONTH and charged ONCE A MONTH. They
 * are the same number, and that is the entire design: the figure on the page is
 * the figure on the bank statement.
 *
 * ── WHY THIS MODULE IS NEARLY EMPTY NOW ────────────────────────────────────
 *
 * Until 2026-08-31 prices were quoted per WEEK and charged monthly, and this
 * module owned the 52/12 conversion between them — `WEEKS_PER_MONTH`,
 * `monthlyFromWeekly()`, and a `billingNote()` that existed purely to print the
 * second number beside the first. All three are gone.
 *
 * The conversion was correct and it was still a liability. Its own comment
 * recorded why: a "$7.99 a week" headline billed as four weeks would overstate
 * what a year costs — "the kind of headline-price mismatch the ACL takes an
 * interest in" — so the multiplier had to be 52/12 and every surface had to
 * remember to print the monthly figure alongside the weekly one. That is a rule
 * enforced by discipline across a dozen components, and discipline is what fails
 * quietly. Quoting monthly removes the second number, so there is nothing to
 * keep in sync and nothing to forget.
 *
 * It also removed a rounding trap. `monthlyFromWeekly` had to round to cents
 * exactly once and hand the rounded value to every caller, because two surfaces
 * rounding the raw product independently is how a page ends up a cent off the
 * invoice. With one number there is no product to round.
 *
 * WHAT DID NOT CHANGE: Stripe is still the system of record for what a customer
 * is charged. This constant is an ADVERTISED rate. If it and the Stripe price
 * ever disagree, Stripe is right and this is the bug — `npm run verify:prices`
 * is what compares them, and it is the gate on any reprice.
 */

/**
 * The standing per-month rate for one model: $4.99 (Ned, 2026-09-03; $19.99 from
 * 2026-08-31 until then).
 *
 * This is the real price of a per-model subscription and what every purchasable
 * entry carries. The founding-member rate is a discount applied on top, so it
 * reverts to this number when the promotion ends — which Terms §6 commits us to
 * telling customers before it happens.
 *
 * Repriced from $14.99/week ($64.96/month) as part of the 2026-08-31 ladder:
 * per-model $19.99, Premium $49.99, Pro $89.99, all monthly — then cut again on
 * 2026-09-03 to $4.99 / $9.99 / $19.99 to meet the competition's price point
 * (kev-docs/kev-token-economics.html models it on measured usage). The cut is
 * affordable because inference is a small fraction of revenue — but it is not
 * uniformly comfortable. At full credit use Pro clears single digits, and under
 * the founding discount both Premium and Pro are sold below cost. That is a
 * decision (Ned, 2026-08-31), not an oversight.
 *
 * ⚠️ THE CREDIT CEILING IS NOW A COST CONTROL, not just a perk. It is what makes
 * "sold below cost" a fixed monthly number rather than an open tab, and it is
 * the only per-user limit left since the spend circuit breaker went blind on the
 * move off Bedrock (api/job_cost_breaker.py). Do not raise `PREMIUM_CREDITS` or
 * `PRO_CREDITS` without redoing the arithmetic.
 */
export const STANDARD_PER_MODEL_MONTHLY_AUD = 4.99;

/**
 * There is no promotional RATE in this module, deliberately.
 *
 * Until 2026-08-27 this held a `BETA_PER_MODEL_WEEKLY_AUD` and a derived monthly
 * discount, because the Beta discount was a fixed-amount Stripe coupon and the
 * site had to advertise the discounted rate. The founding promotion is a
 * PERCENTAGE coupon instead — 30% off, `duration=forever`, capped at the first
 * 20 redemptions.
 *
 * ⚠️ IT IS APPLIED BY THE SERVER, NOT TYPED BY THE CUSTOMER. RIPK's
 * `create_checkout_session` attaches the discount itself from `BETA_COUPON_ID`,
 * and Stripe makes `discounts` and `allow_promotion_codes` MUTUALLY EXCLUSIVE —
 * so the session carries no code box, and nothing in this app renders one.
 *
 * A promotion CODE object is involved, and that is not a contradiction. It is
 * applied server-side as `discounts[0][promotion_code]` because the cap that
 * makes this "the first 20" lives on the promotion code rather than on the
 * coupon beneath it — the customer still types nothing and still sees no code
 * field. Naming the coupon id instead would apply an unlimited discount that no
 * cap governs; handler_billing.py records that trap in full.
 *
 * `duration=forever` is deliberate and contractual. `repeating` would jump these
 * 20 accounts to full price when the interval lapsed, which
 * kev-nba-limited-launch-plan.md flags as the one setting here with a legal
 * edge. They are founding members, not a trial — and the copy has to say so,
 * because "introductory" promises the opposite of what the coupon does.
 *
 * What that costs, and what has to make up for it: NO SURFACE HERE SAYS THE
 * DISCOUNT EXISTS. Every page shows the standing rate and the 30% first appears
 * on Stripe's own checkout. Someone arriving from the campaign after redemption
 * 20 sees full price with nothing explaining why, so the ad has to carry the
 * "first 20" limit in its own words — this app will not say it, and cannot,
 * without asking Stripe how many have been redeemed.
 *
 * A percentage also needs no per-product amount, which is the whole reason it
 * was chosen: one fixed dollar discount cannot be exactly right for three
 * different prices, and a percentage is exactly right for every one of them.
 *
 * Every price in this file is therefore the standing rate, and every surface
 * shows it. The discount exists only in Stripe, and appears only at checkout.
 */
